// src/pages/orders/SalesOrderApproval.jsx
import { useEffect, useState } from "react";
import {
  Button,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Box,
  Grid,
  Paper,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MantisDataTable from "../../../components/common/MantisDataTable";
import { useAuth } from "../../../sections/auth/AuthContext";
import { fetchState } from "../../../api/locationApi";
import { fetchorders, upsertOrders } from "../../../api/salesOrderApi";

const OrderStatus = {
  DISPATCHED: "DISPATCHED",
  IN_TRANSIT: "IN_TRANSIT",
  DELIVERED: "DELIVERED",
  CANCELLED: "CANCELLED",
  REJECTED: "REJECTED",
};

const SalesOrderApproval = () => {
  const [orderData, setOrderData] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isCompletedView, setIsCompletedView] = useState(false);
  const [remark, setRemark] = useState("");
  const [status, setStatus] = useState("");
  const [tabIndex, setTabIndex] = useState(0);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [completedState, setCompletedState] = useState("");
  const [completedStartDate, setCompletedStartDate] = useState("");
  const [completedEndDate, setCompletedEndDate] = useState("");
  const { userInfo } = useAuth();

  // ✅ Fetch active (non-completed) orders
  const loadOrders = async (state = "", start = "", end = "") => {
    try {
      const data = await fetchorders(state, "", start, end);
      const excludedStatuses = ["DELIVERED", "CANCELLED", "REJECTED", "SUBMITTED"];
      const filtered = (data || []).filter(
        (item) => !excludedStatuses.includes(item.status?.toUpperCase())
      );
      const formattedData = filtered.map((item) => ({
        ...item,
        order_date: item.order_date ? item.order_date.split("T")[0] : "",
        distributor_state: item.distributor_state?.toLowerCase() || "unknown",
      }));
      setOrderData(formattedData);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      toast.error("Failed to fetch orders");
    }
  };

  // ✅ Fetch completed (DELIVERED) orders
  const loadCompletedOrders = async (state = "", start = "", end = "") => {
    try {
      const data = await fetchorders(state, "", start, end);
      const delivered = (data || []).filter(
        (item) => item.status?.toUpperCase() === "DELIVERED"
      );
      const formatted = delivered.map((item) => ({
        ...item,
        order_date: item.order_date ? item.order_date.split("T")[0] : "",
        distributor_state: item.distributor_state?.toLowerCase() || "unknown",
      }));
      setCompletedOrders(formatted);
    } catch (err) {
      console.error("Failed to fetch completed orders:", err);
      toast.error("Failed to fetch completed orders");
    }
  };

  // ✅ Fetch States
  const loadStates = async () => {
    try {
      const res = await fetchState();
      setStates(res || []);
    } catch (err) {
      console.error("Error fetching states:", err);
    }
  };

  useEffect(() => {
    loadStates();
  }, []);

  const handleEditClick = (row) => {
    setSelectedOrder(row);
    setRemark("");
    setStatus("");
    setIsCompletedView(false);
  };

  const handleViewClick = (row) => {
    setSelectedOrder(row);
    setIsCompletedView(true);
  };

  const handleBackToList = () => {
    setSelectedOrder(null);
    setRemark("");
    setStatus("");
    setIsCompletedView(false);
  };

  const handleUpdateOrder = async () => {
    if (!status.trim()) return toast.error("Status is required!");
    if (!remark.trim()) return toast.error("Remark is required!");

    const payload = {
      order_id: selectedOrder.order_id,
      new_status: status,
      changed_by: userInfo?.empCode || "UNKNOWN",
      note: remark,
    };

    try {
      const result = await upsertOrders(payload);
      const isSuccess =
        result?.success === true ||
        result?.apiErrorCode === 0 ||
        result?.status?.toString()?.toLowerCase?.() === "success";

      if (isSuccess) {
        toast.success("Order updated successfully!");
        handleBackToList();
        loadOrders(selectedState, startDate, endDate);
      } else toast.error("Failed to update order.");
    } catch (err) {
      console.error("Error updating order:", err);
      toast.error("Error updating order.");
    }
  };

  const filteredOrders = selectedState
    ? orderData.filter((o) => o.distributor_state === selectedState.toLowerCase())
    : orderData;

  const filteredCompleted = completedState
    ? completedOrders.filter((o) => o.distributor_state === completedState.toLowerCase())
    : completedOrders;

  return (
    <>
      {!selectedOrder ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)}>
              <Tab label="Orders in Process" />
              <Tab label="Completed Orders" />
            </Tabs>
          </Box>

          {/* ✅ Orders in Process */}
          {tabIndex === 0 && (
            <MantisDataTable
              title="Sales Order Approval"
              data={filteredOrders}
              columns={[
                {
                  label: "Action",
                  type: "actions",
                  actions: [{ type: "edit", label: "Edit", action: handleEditClick }],
                },
                { key: "order_id", label: "Order ID" },
                { key: "customer_name", label: "Customer Name" },
                { key: "placed_by_name", label: "Placed By" },
                { key: "order_total", label: "Total (₹)" },
                { key: "approver_name", label: "Approved By" },
                { key: "status", label: "Status", type: "badge" },
                { key: "order_date", label: "Order Date" },
              ]}
              filters={[
                {
                  label: "State",
                  name: "state",
                  type: "select",
                  options: [
                    { label: "All States", value: "" },
                    ...states.map((s) => ({
                      label: s.description,
                      value: s.description,
                    })),
                  ],
                  defaultValue: selectedState,
                },
                { label: "Start Date", name: "start_date", type: "date" },
                { label: "End Date", name: "end_date", type: "date" },
              ]}
              onFilterChange={(form) => {
                const { state, start_date, end_date } = form;
                setSelectedState(state || "");
                setStartDate(start_date || "");
                setEndDate(end_date || "");
                if (start_date && end_date) loadOrders(state, start_date, end_date);
                else setOrderData([]);
              }}
            />
          )}

          {/* ✅ Completed Orders */}
          {tabIndex === 1 && (
            <MantisDataTable
              title="Delivered Orders"
              data={filteredCompleted}
              columns={[
                {
                  label: "Action",
                  type: "actions",
                  actions: [{ type: "view", label: "View", action: handleViewClick }],
                },
                { key: "order_id", label: "Order ID" },
                { key: "customer_name", label: "Customer Name" },
                { key: "placed_by_name", label: "Placed By" },
                { key: "order_total", label: "Total (₹)" },
                { key: "approver_name", label: "Approved By" },
                { key: "status", label: "Status", type: "badge" },
                { key: "order_date", label: "Order Date" },
              ]}
              filters={[
                {
                  label: "State",
                  name: "state",
                  type: "select",
                  options: [
                    { label: "All States", value: "" },
                    ...states.map((s) => ({
                      label: s.description,
                      value: s.description,
                    })),
                  ],
                  defaultValue: completedState,
                },
                { label: "Start Date", name: "start_date", type: "date" },
                { label: "End Date", name: "end_date", type: "date" },
              ]}
              onFilterChange={(form) => {
                const { state, start_date, end_date } = form;
                setCompletedState(state || "");
                setCompletedStartDate(start_date || "");
                setCompletedEndDate(end_date || "");
                if (start_date && end_date) loadCompletedOrders(state, start_date, end_date);
                else setCompletedOrders([]);
              }}
            />
          )}
        </>
      ) : (
        <Box sx={{ p: 3 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleBackToList}
            sx={{ mb: 2, textTransform: "none" }}
          >
            ← Back to Orders
          </Button>

          <Paper elevation={3} sx={{ p: 3, borderRadius: "12px", backgroundColor: "#fff" }}>
            <h3>Order Details (#{selectedOrder.order_id})</h3>

            {/* ✅ Order Info */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                mb: 3,
                backgroundColor: "#f9f9f9",
                p: 2,
                borderRadius: "8px",
              }}
            >
              <Box>
                <strong>Customer Name:</strong>
                <div>{selectedOrder.customer_name || "-"}</div>
              </Box>
              <Box>
                <strong>Placed By:</strong>
                <div>{selectedOrder.placed_by_name || "-"}</div>
              </Box>
              <Box>
                <strong>Order Date:</strong>
                <div>{selectedOrder.order_date || "-"}</div>
              </Box>
              <Box>
                <strong>Total Amount (₹):</strong>
                <div style={{ fontWeight: "bold", color: "#1976d2" }}>
                  {selectedOrder.order_total || 0}
                </div>
              </Box>
            </Box>

            {/* ✅ Timeline (Creative version with icons) */}
            <Box sx={{ mt: 3, mb: 3 }}>
              <h4>Order Progress</h4>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 2,
                  position: "relative",
                }}
              >
                {[
                  { label: "APPROVED", icon: "✅" },
                  { label: "DISPATCHED", icon: "📦" },
                  { label: "IN_TRANSIT", icon: "🚚" },
                  { label: "DELIVERED", icon: "🏠" },
                ].map((step, index) => {
                  const currentStatus = isCompletedView
                    ? "DELIVERED"
                    : selectedOrder.status?.toUpperCase() || "APPROVED";
                  const steps = ["APPROVED", "DISPATCHED", "IN_TRANSIT", "DELIVERED"];
                  const currentIndex = steps.indexOf(currentStatus);
                  const isCompleted = index <= currentIndex;

                  return (
                    <Box
                      key={step.label}
                      sx={{ textAlign: "center", flex: 1, position: "relative" }}
                    >
                      {/* Connector line */}
                      {index < steps.length - 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "16px",
                            left: "50%",
                            right: "-50%",
                            height: "3px",
                            backgroundColor: index < currentIndex ? "#1976d2" : "#ccc",
                            transition: "background-color 0.4s ease",
                            zIndex: 0,
                          }}
                        />
                      )}

                      {/* Icon circle */}
                      <Box
                        sx={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          backgroundColor: isCompleted ? "#1976d2" : "#e0e0e0",
                          color: "#fff",
                          margin: "0 auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "16px",
                          boxShadow: isCompleted
                            ? "0 0 6px rgba(25,118,210,0.4)"
                            : "none",
                          zIndex: 1,
                          position: "relative",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {step.icon}
                      </Box>

                      {/* Label */}
                      <Box
                        sx={{
                          mt: 1,
                          fontSize: "13px",
                          fontWeight: 500,
                          color: isCompleted ? "#1976d2" : "#666",
                          transition: "color 0.3s ease",
                        }}
                      >
                        {step.label.replace("_", " ")}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>


            {/* ✅ Ordered Products with Single Edit for Whole Table */}
            <h4 style={{ marginTop: "10px" }}>Ordered Products</h4>

            <Box
              sx={{
                mt: 1,
                overflowX: "auto",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                {!isCompletedView && (
                  <>
                    {!selectedOrder.isEditing ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          setSelectedOrder({
                            ...selectedOrder,
                            isEditing: true,
                            backupProducts: [...selectedOrder.product_list],
                          })
                        }
                        sx={{ mb: 1, textTransform: "none" }}
                      >
                        ✏️ Edit
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          sx={{ mr: 1, mb: 1, textTransform: "none" }}
                          onClick={() => {
                            const updatedList = selectedOrder.product_list.map((p) => ({
                              ...p,
                              isEditing: false,
                            }));
                            setSelectedOrder({
                              ...selectedOrder,
                              isEditing: false,
                              product_list: updatedList,
                            });
                            toast.success("Quantities updated successfully!");
                          }}
                        >
                          💾 Save
                        </Button>

                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          sx={{ mb: 1, textTransform: "none" }}
                          onClick={() => {
                            const backup =
                              selectedOrder.backupProducts || selectedOrder.product_list;
                            setSelectedOrder({
                              ...selectedOrder,
                              isEditing: false,
                              product_list: backup,
                            });
                          }}
                        >
                          ❌ Cancel
                        </Button>
                      </>
                    )}
                  </>
                )}
              </Box>

            </Box>

            <Box sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5", textAlign: "left" }}>
                    <th style={{ border: "1px solid #ddd", padding: "6px", width: "20%" }}>Product Code</th>
                    <th style={{ border: "1px solid #ddd", padding: "6px", width: "20%" }}>Product Name</th>
                    <th style={{ border: "1px solid #ddd", padding: "6px", width: "20%" }}>Variant</th>
                    <th style={{ border: "1px solid #ddd", padding: "6px", width: "15%" }}>Qty</th>
                    <th
                      style={{
                        border: "1px solid #ddd",
                        padding: "6px",
                        width: "20%",
                        textAlign: "right",
                      }}
                    >
                      Amount (₹)
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {selectedOrder.product_list?.map((p, i) => {
                    const handleQtyChange = (e) => {
                      const newQty = Number(e.target.value) || 0;
                      const updatedList = selectedOrder.product_list.map((item, idx) =>
                        idx === i
                          ? {
                            ...item,
                            quantity_ordered: newQty,
                            product_total: (newQty * (item.unit_price || 0)).toFixed(2),
                          }
                          : item
                      );
                      setSelectedOrder({ ...selectedOrder, product_list: updatedList });
                    };

                    return (
                      <tr key={i}>
                        <td style={{ border: "1px solid #ddd", padding: "6px" }}>{p.product_code}</td>
                        <td style={{ border: "1px solid #ddd", padding: "6px" }}>{p.product_name}</td>
                        <td style={{ border: "1px solid #ddd", padding: "6px" }}>{p.variant_name}</td>
                        <td style={{ border: "1px solid #ddd", padding: "6px" }}>
                          {selectedOrder.isEditing ? (
                            <input
                              type="number"
                              min="1"
                              value={p.quantity_ordered}
                              onChange={handleQtyChange}
                              style={{
                                width: "80%",
                                padding: "4px",
                                borderRadius: "4px",
                                border: "1px solid #ccc",
                              }}
                            />
                          ) : (
                            p.quantity_ordered
                          )}
                        </td>
                        <td
                          style={{
                            border: "1px solid #ddd",
                            padding: "6px",
                            textAlign: "right",
                          }}
                        >
                          {p.product_total}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                <tfoot>
                  <tr>
                    <td
                      colSpan="4"
                      style={{
                        border: "none",
                        padding: "0",
                        backgroundColor: "#fff",
                      }}
                    ></td>
                    <td
                      style={{
                        borderTop: "2px solid #000",
                        padding: "8px 12px",
                        textAlign: "right",
                        fontWeight: "bold",
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      Total Amount (₹):{" "}
                      <span style={{ color: "#1976d2" }}>
                        {selectedOrder.product_list?.reduce(
                          (sum, p) => sum + Number(p.product_total || 0),
                          0
                        )}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </Box>



            {/* ✅ Status & Update Button — Hidden for completed view */}
            {!isCompletedView && (
              <>
                <h4 style={{ marginTop: "10px" }}>Update Order</h4>
                <Box sx={{ mt: 3 }}>
                  <TextField
                    select
                    fullWidth
                    label="Select Status *"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    margin="dense"
                  >
                    <MenuItem value={OrderStatus.DISPATCHED}>DISPATCHED</MenuItem>
                    <MenuItem value={OrderStatus.IN_TRANSIT}>IN TRANSIT</MenuItem>
                    <MenuItem value={OrderStatus.DELIVERED}>DELIVERED</MenuItem>
                    <MenuItem value={OrderStatus.CANCELLED}>CANCELLED</MenuItem>
                    <MenuItem value={OrderStatus.REJECTED}>REJECTED</MenuItem>
                  </TextField>

                  <TextField
                    fullWidth
                    label="Remark *"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    margin="dense"
                    multiline
                    rows={3}
                    placeholder="Enter remark"
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={handleUpdateOrder}
                  >
                    Update Order
                  </Button>
                </Box>
              </>
            )}

          </Paper>
        </Box>
      )}
    </>
  );
};

export default SalesOrderApproval;